import React, { useCallback, useEffect, useRef, useState } from "react";
import styled, { css, keyframes } from "styled-components";
import * as amplitude from "@amplitude/analytics-browser";

export const SCRATCH_DISCOUNT_CODE = "SPECIAL20";

const AUTO_REVEAL_DELAY_MS = 15000;
const AUTO_CONTINUE_SECONDS = 5;
const SCRATCH_REVEAL_THRESHOLD = 0.5;
const SCRATCH_BRUSH_SIZE = 34;
const DISCOUNT_STORAGE_KEY = "BWS_REVEALED_DISCOUNT_CODE";

type RevealMethod = "scratch" | "timeout";

type ScratchDiscountPageProps = {
  onComplete: () => void;
  discountCode?: string;
};

const fadeUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(18px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const enter = css`
  opacity: 0;
  animation: ${fadeUp} 0.45s ease-out forwards;
`;

const Page = styled.div`
  width: 100%;
  min-height: 100vh;
  background: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  box-sizing: border-box;
  font-family: "Lato", sans-serif;
`;

const Shell = styled.div`
  width: min(100%, 520px);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const Logo = styled.img`
  width: 142px;
  height: auto;
  margin-bottom: 28px;
  ${enter}
  animation-delay: 0.05s;
`;

const Title = styled.h1`
  ${enter}
  animation-delay: 0.12s;
  margin: 0;
  max-width: 460px;
  font-family: "Oswald", sans-serif;
  color: #1e1e1e;
  font-size: 38px;
  font-weight: 600;
  line-height: 1.12;

  @media (max-width: 520px) {
    font-size: 30px;
  }
`;

const Subtitle = styled.p`
  ${enter}
  animation-delay: 0.18s;
  margin: 14px 0 24px;
  max-width: 420px;
  color: #4a4a4a;
  font-size: 17px;
  font-weight: 600;
  line-height: 1.45;

  @media (max-width: 520px) {
    font-size: 15px;
    margin-bottom: 20px;
  }
`;

const Ticket = styled.div`
  ${enter}
  animation-delay: 0.26s;
  width: min(100%, 360px);
  background: #0b6dff;
  border-radius: 18px;
  padding: 16px;
  box-sizing: border-box;
  color: #fff;
  position: relative;
  box-shadow: 0 10px 24px rgba(11, 109, 255, 0.16);

  &:before,
  &:after {
    content: "";
    position: absolute;
    top: calc(62% - 18px);
    width: 34px;
    height: 34px;
    border-radius: 50%;
    background: #f5f5f5;
    z-index: 2;
  }

  &:before {
    left: -17px;
  }

  &:after {
    right: -17px;
  }
`;

const RevealPanel = styled.div`
  position: relative;
  min-height: 210px;
  border: 1px solid rgba(255, 255, 255, 0.28);
  border-radius: 14px;
  overflow: hidden;
  background: linear-gradient(180deg, #0b6dff 0%, #075bd7 100%);
`;

const RevealContent = styled.div`
  min-height: 210px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 28px;
  box-sizing: border-box;
`;

const DiscountValue = styled.div`
  font-family: "Oswald", sans-serif;
  font-size: 84px;
  font-weight: 600;
  line-height: 0.95;
  letter-spacing: 0;

  @media (max-width: 520px) {
    font-size: 72px;
  }
`;

const DiscountLabel = styled.div`
  margin-top: 8px;
  font-size: 28px;
  font-weight: 800;
  line-height: 1;
`;

const DiscountSubtext = styled.div`
  margin-top: 16px;
  max-width: 220px;
  font-size: 17px;
  font-weight: 700;
  line-height: 1.35;
`;

const ScratchCanvas = styled.canvas<{ $isRevealed: boolean }>`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: ${({ $isRevealed }) => ($isRevealed ? 0 : 1)};
  pointer-events: ${({ $isRevealed }) => ($isRevealed ? "none" : "auto")};
  transition: opacity 0.35s ease-out;
  cursor: grab;
  touch-action: none;

  &:active {
    cursor: grabbing;
  }
`;

const Divider = styled.div`
  position: relative;
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:before {
    content: "";
    width: 100%;
    border-top: 4px dashed #171717;
  }
`;

const CodePanel = styled.div<{ $isRevealed: boolean }>`
  background: #171717;
  border-radius: 14px;
  padding: 18px 16px 20px;
  opacity: ${({ $isRevealed }) => ($isRevealed ? 1 : 0.16)};
  transform: translateY(${({ $isRevealed }) => ($isRevealed ? "0" : "6px")});
  transition: opacity 0.3s ease-out, transform 0.3s ease-out;
`;

const CodeLabel = styled.div`
  color: #a7a7a7;
  font-size: 15px;
  font-weight: 800;
  line-height: 1;
`;

const CodeValue = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 10px;
  color: #fff;
  font-family: "Oswald", sans-serif;
  font-size: 34px;
  font-weight: 600;
  letter-spacing: 0.03em;
  line-height: 1;
`;

const CheckIcon = styled.svg`
  width: 20px;
  height: 20px;
  color: #34c759;
  flex: 0 0 auto;
`;

const StatusText = styled.p`
  ${enter}
  animation-delay: 0.34s;
  min-height: 24px;
  margin: 18px 0 0;
  color: #5f6368;
  font-size: 15px;
  font-weight: 700;
  line-height: 1.4;
`;

const FooterActions = styled.div`
  ${enter}
  animation-delay: 0.4s;
  min-height: 52px;
  margin-top: 18px;
  display: flex;
  justify-content: center;
`;

const ContinueButton = styled.button`
  min-width: 212px;
  border: 0;
  border-radius: 8px;
  background: #dfb140;
  color: #111;
  cursor: pointer;
  font-family: "Oswald", sans-serif;
  font-size: 24px;
  font-weight: 600;
  line-height: 1;
  padding: 15px 22px;
  text-transform: uppercase;
  transition: background-color 0.18s ease, color 0.18s ease;

  &:hover {
    background: #0b6dff;
    color: #fff;
  }

  &:focus-visible {
    outline: 2px solid #0b6dff;
    outline-offset: 3px;
  }
`;

const roundRect = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number
) => {
  const normalizedRadius = Math.min(radius, width / 2, height / 2);
  ctx.beginPath();
  ctx.moveTo(x + normalizedRadius, y);
  ctx.arcTo(x + width, y, x + width, y + height, normalizedRadius);
  ctx.arcTo(x + width, y + height, x, y + height, normalizedRadius);
  ctx.arcTo(x, y + height, x, y, normalizedRadius);
  ctx.arcTo(x, y, x + width, y, normalizedRadius);
  ctx.closePath();
};

export function ScratchDiscountPage({
  onComplete,
  discountCode = SCRATCH_DISCOUNT_CODE,
}: ScratchDiscountPageProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const isPointerDownRef = useRef(false);
  const isRevealedRef = useRef(false);
  const scratchMoveCountRef = useRef(0);
  const hasCompletedRef = useRef(false);
  const [isRevealed, setIsRevealed] = useState(false);
  const [countdown, setCountdown] = useState(AUTO_CONTINUE_SECONDS);

  const finish = useCallback(() => {
    if (hasCompletedRef.current) {
      return;
    }

    hasCompletedRef.current = true;
    amplitude.track("ScratchDiscountContinue", {
      discountCode,
    });
    onComplete();
  }, [discountCode, onComplete]);

  const reveal = useCallback(
    (method: RevealMethod) => {
      if (isRevealedRef.current) {
        return;
      }

      isRevealedRef.current = true;
      setIsRevealed(true);
      setCountdown(AUTO_CONTINUE_SECONDS);
      try {
        sessionStorage.setItem(DISCOUNT_STORAGE_KEY, discountCode);
      } catch {
        // Storage can be unavailable in private browsing contexts.
      }
      amplitude.track("ScratchDiscountRevealed", {
        discountCode,
        method,
      });
    },
    [discountCode]
  );

  const drawScratchCover = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || isRevealedRef.current) {
      return;
    }

    const rect = canvas.getBoundingClientRect();
    const ratio = window.devicePixelRatio || 1;
    const width = Math.max(1, Math.floor(rect.width * ratio));
    const height = Math.max(1, Math.floor(rect.height * ratio));

    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext("2d");
    if (!ctx) {
      return;
    }

    ctx.setTransform(ratio, 0, 0, ratio, 0, 0);

    const gradient = ctx.createLinearGradient(0, 0, rect.width, rect.height);
    gradient.addColorStop(0, "#1f7cff");
    gradient.addColorStop(1, "#075bd7");
    ctx.fillStyle = gradient;
    roundRect(ctx, 0, 0, rect.width, rect.height, 14);
    ctx.fill();

    ctx.globalAlpha = 0.18;
    ctx.strokeStyle = "#ffffff";
    ctx.lineWidth = 2;
    for (let x = -rect.height; x < rect.width; x += 24) {
      ctx.beginPath();
      ctx.moveTo(x, rect.height);
      ctx.lineTo(x + rect.height, 0);
      ctx.stroke();
    }
    ctx.globalAlpha = 1;

    ctx.fillStyle = "#ffffff";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.font = "700 28px Lato, sans-serif";
    ctx.fillText("Scratch here", rect.width / 2, rect.height / 2 - 8);
    ctx.font = "600 15px Lato, sans-serif";
    ctx.fillText("Your discount is underneath", rect.width / 2, rect.height / 2 + 28);
  }, []);

  useEffect(() => {
    amplitude.track("ScratchDiscountViewed", {
      discountCode,
    });
  }, [discountCode]);

  useEffect(() => {
    drawScratchCover();

    const canvas = canvasRef.current;
    if (!canvas || typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", drawScratchCover);
      return () => window.removeEventListener("resize", drawScratchCover);
    }

    const observer = new ResizeObserver(drawScratchCover);
    observer.observe(canvas);
    return () => observer.disconnect();
  }, [drawScratchCover]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      reveal("timeout");
    }, AUTO_REVEAL_DELAY_MS);

    return () => window.clearTimeout(timeoutId);
  }, [reveal]);

  useEffect(() => {
    if (!isRevealed) {
      return;
    }

    if (countdown <= 0) {
      finish();
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setCountdown((currentCountdown) => currentCountdown - 1);
    }, 1000);

    return () => window.clearTimeout(timeoutId);
  }, [countdown, finish, isRevealed]);

  const getScratchPoint = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return null;
    }

    const rect = canvas.getBoundingClientRect();
    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };
  };

  const getClearedRatio = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) {
      return 0;
    }

    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
    let cleared = 0;
    let total = 0;

    for (let index = 3; index < imageData.length; index += 64) {
      total += 1;
      if (imageData[index] < 24) {
        cleared += 1;
      }
    }

    return total === 0 ? 0 : cleared / total;
  };

  const scratchAt = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (isRevealedRef.current) {
      return;
    }

    const point = getScratchPoint(event);
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!point || !ctx) {
      return;
    }

    ctx.globalCompositeOperation = "destination-out";
    ctx.beginPath();
    ctx.arc(point.x, point.y, SCRATCH_BRUSH_SIZE, 0, Math.PI * 2);
    ctx.fill();
    ctx.globalCompositeOperation = "source-over";

    scratchMoveCountRef.current += 1;

    if (
      scratchMoveCountRef.current % 5 === 0 &&
      getClearedRatio() >= SCRATCH_REVEAL_THRESHOLD
    ) {
      reveal("scratch");
    }
  };

  const handlePointerDown = (event: React.PointerEvent<HTMLCanvasElement>) => {
    isPointerDownRef.current = true;
    event.currentTarget.setPointerCapture(event.pointerId);
    scratchAt(event);
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (!isPointerDownRef.current) {
      return;
    }

    scratchAt(event);
  };

  const handlePointerUp = (event: React.PointerEvent<HTMLCanvasElement>) => {
    isPointerDownRef.current = false;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }

    if (!isRevealedRef.current && getClearedRatio() >= SCRATCH_REVEAL_THRESHOLD) {
      reveal("scratch");
    }
  };

  return (
    <Page>
      <Shell>
        <Logo src="/logo2.svg" alt="Built With Science" />
        <Title>Scratch to unlock your special discount!</Title>
        <Subtitle>
          We want you to start your personalized plan with a nice surprise
        </Subtitle>

        <Ticket aria-live="polite">
          <RevealPanel>
            <RevealContent>
              <DiscountValue>20%</DiscountValue>
              <DiscountLabel>discount</DiscountLabel>
              <DiscountSubtext>on your personalized BWS+ plan</DiscountSubtext>
            </RevealContent>
            <ScratchCanvas
              ref={canvasRef}
              $isRevealed={isRevealed}
              aria-label="Scratch to reveal your discount"
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onPointerCancel={handlePointerUp}
            />
          </RevealPanel>

          <Divider />

          <CodePanel $isRevealed={isRevealed}>
            <CodeLabel>Promo code</CodeLabel>
            <CodeValue>
              {isRevealed && (
                <CheckIcon viewBox="0 0 20 20" aria-hidden="true">
                  <path
                    d="M7.8 14.6 3.7 10.5l1.3-1.3 2.8 2.8 7.2-7.2 1.3 1.3-8.5 8.5Z"
                    fill="currentColor"
                  />
                </CheckIcon>
              )}
              {isRevealed ? discountCode : "--------"}
            </CodeValue>
          </CodePanel>
        </Ticket>

        {isRevealed && (
          <>
            <StatusText>
              {`Applied automatically at checkout. Continuing in ${countdown}.`}
            </StatusText>
            <FooterActions>
              <ContinueButton onClick={finish} type="button">
                Get My Plan
              </ContinueButton>
            </FooterActions>
          </>
        )}
      </Shell>
    </Page>
  );
}
